import React, { useEffect, useRef, useImperativeHandle, forwardRef, useId } from 'react';
import { Platform } from 'react-native';
import { TURNSTILE_SITE_KEY } from '@/constants/config';
import { useTheme } from '@/context/ThemeContext';

const darkThemes = ["sepia", "muted", "night", "smoke", "black"];
const MAX_RESET_ATTEMPTS = 3;

const Turnstile = forwardRef(({ onError }, ref) => {
  const widgetId = useRef(null);
  const resetAttempts = useRef(0);
  const promiseHandlers = useRef(null);
  const { theme } = useTheme();
  
  // Use unique ID for each instance to support multiple forms on same page
  const containerId = `turnstile-${useId().replace(/:/g, '')}`;

  // Expose execute method that returns a Promise with the token
  useImperativeHandle(ref, () => ({
    execute: () => {
      return new Promise(async (resolve, reject) => {
        try {
          // Store handlers so callbacks can resolve/reject
          promiseHandlers.current = { resolve, reject };
          resetAttempts.current = 0;

          // Check if we're in a web environment
          if (typeof window === 'undefined') {
            reject(new Error('Turnstile can only be executed in a web environment'));
            return;
          }

          // Wait for turnstile script if not loaded yet
          if (!window.turnstile) {
            // Try waiting up to 3 seconds for script to load
            for (let i = 0; i < 6; i++) {
              await new Promise(r => setTimeout(r, 500));
              if (window.turnstile) break;
            }
            if (!window.turnstile) {
              reject(new Error('Turnstile script failed to load'));
              return;
            }
          }

          if (!TURNSTILE_SITE_KEY) {
            reject(new Error('Turnstile site key is not configured'));
            return;
          }

          const container = document.getElementById(containerId);
          if (!container) {
            reject(new Error('Turnstile container not found'));
            return;
          }

          // Remove existing widget if any
          if (widgetId.current) {
            try {
              window.turnstile.remove(widgetId.current);
              widgetId.current = null;
            } catch (e) {
              // Ignore removal errors
            }
          }

          // Render and execute widget
          widgetId.current = window.turnstile.render(`#${containerId}`, {
            sitekey: TURNSTILE_SITE_KEY,
            size: 'flexible',
            execution: 'execute',
            callback: (token) => {
              resetAttempts.current = 0;
              promiseHandlers.current?.resolve(token);
              promiseHandlers.current = null;
            },
            'expired-callback': () => {
              promiseHandlers.current?.reject(new Error('Verification expired'));
              promiseHandlers.current = null;
            },
            'error-callback': (error) => {
              console.error('Turnstile error:', error);
              
              if (resetAttempts.current >= MAX_RESET_ATTEMPTS) {
                promiseHandlers.current?.reject(new Error('Verification failed after multiple attempts'));
                promiseHandlers.current = null;
                return;
              }

              resetAttempts.current += 1;
              
              // Try to reset the widget
              try {
                if (widgetId.current) {
                  window.turnstile.reset(widgetId.current);
                }
              } catch (resetError) {
                promiseHandlers.current?.reject(new Error('Verification failed'));
                promiseHandlers.current = null;
              }
            },
            theme: darkThemes.includes(theme) ? 'dark' : 'light',
          });

          // Execute the challenge
          window.turnstile.execute(widgetId.current);
        } catch (error) {
          console.error('Turnstile execution error:', error);
          onError?.(error.message || 'Failed to initialize verification');
          reject(error);
          promiseHandlers.current = null;
        }
      });
    }
  }));

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined' && window.turnstile && widgetId.current) {
        try {
          window.turnstile.remove(widgetId.current);
        } catch (error) {
          // Ignore cleanup errors
        }
      }
      // Reject any pending promise
      promiseHandlers.current?.reject(new Error('Component unmounted'));
      promiseHandlers.current = null;
    };
  }, []);

  // Don't render on non-web platforms
  if (Platform.OS !== 'web') {
    return null;
  }

  return <div id={containerId} />;
});

Turnstile.displayName = 'Turnstile';

export default Turnstile;
